import { Currency, FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { canUseDOM } from '@ps/web-core-utils';
import { useTrackers } from '@ps/web-shared-analytics';
import classNames from 'clsx';
import { get, groupBy, isEmpty } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import type { CasinoIcon } from '@swf/casino';
import { CasinoGameIcon, CasinoPlaySelector, GameInfoIcon, getIconClassKeys } from '@swf/casino';
import JackpotRibbon from '@swf/components/JackpotRibbon/JackpotRibbon';
import fsbIcon from '@swf/core/assets/casino/freeSpin-icon-sm.png';
import multiIcon from '@swf/core/assets/casino/multi.png';
import FavIcon from '@swf/core/assets/casino/my-games-favorite-icon.svg';
import singleMultiIcon from '@swf/core/assets/casino/single-multi.png';
import useTouchSupported from '@swf/core/hooks/useTouchSupported';
import { useWidget } from '@swf/core/hooks/useWidget';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { CasinoGame } from '@swf/core/types/CasinoGame';
import { MosaicContext, PlayType } from '@swf/core/types/CasinoGame';
import type { CasinoRace } from '@swf/core/types/CasinoRaces';
import { isLiveCasinoGame, isRunning } from '@swf/core/utils/casino';

import { TXTCLI_Free_Spins } from './CasinoGameTile.messages.json';
import styles from './CasinoGameTile.scss';
import Ribbon from './components/Ribbon/Ribbon';

export interface Props {
  className?: string;
  count?: number;
  fsbLaunch?: boolean;
  infoIconClass?: string;
  game: CasinoGame;
  launchSource?: string;
  race?: CasinoRace;
  icon?: CasinoIcon;
  index?: number;
  noIconScale?: boolean;
  hideFeatureButtons?: boolean;
  onLaunch?(game: CasinoGame): void;
  preload?: boolean;
  favouriteIcon?: JSX.Element;
  liveRaceIcon?: boolean;
  mosaicHint?: MosaicContext;
  gameDeckMosaic?: boolean;
  jackpotClassName?: string;
  gameCategory?: string;
}

const CasinoGameTile: React.FC<Props> = ({
  className,
  count,
  fsbLaunch,
  game,
  race,
  icon,
  index,
  noIconScale,
  infoIconClass,
  onLaunch,
  preload,
  favouriteIcon,
  liveRaceIcon = true,
  mosaicHint,
  hideFeatureButtons,
  launchSource,
  jackpotClassName,
  gameCategory
}) => {
  const favoritesEnabled = useFeature(FeatureFlag.CasinoFavorites);
  const hasInfoRoute = useSelector(select.casino.hasInfoRoute);
  const isMobile = useSelector(select.device.isMobile);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const isTouchSupported = useTouchSupported();
  const { casino } = useDispatch<Dispatch>();
  const [isVisible, setVisible] = useState(false);
  const { casinoGameLaunched } = useTrackers();
  const { widgetId } = useWidget();

  const { formatMessage } = useIntl();
  const { jackpot, name, fsb } = game;
  const tables = useMemo(() => groupBy(game.tables, 'playType'), [game.tables]);
  const playMoney = !!get(tables, [PlayType.Single, 0, 'playMoney']);

  const launchGame = (playType?: PlayType, label?: string) => {
    casinoGameLaunched({
      game: { ...game, gameCategory },
      playType,
      index,
      totalGames: count,
      buttonName: label,
      action: launchSource
    });
    if (race) {
      casino.race({
        game,
        raceId: race.id,
        raceTypes: race.raceTypes,
        tncVersion: race.tncVersion,
        join: !isRunning(race.status),
        launch: !isRunning(race.status),
        widgetId
      });
    } else if (game) {
      casino.launch({ game, playType, fsbLaunch, widgetId });
    }
    if (onLaunch) {
      onLaunch(game);
    }
  };

  const onFavoriteClick = useCallback(() => {
    if (game.gametype && game.variant)
      casino.toggleFavoriteGame({
        gameTypeId: game.gametype,
        gameVariantId: game.variant,
        action: game.isFavorite ? 'Remove' : 'Add'
      });
  }, [casino, game]);

  const iconClass = getIconClassKeys(icon || 'icnSquare').map(
    (val: string) => styles[val as keyof typeof styles]
  );

  const { ref } = useInView({
    onChange: setVisible,
    triggerOnce: true,
    rootMargin: canUseDOM && !isMobile ? `0px 50px 50px 0px` : undefined
  });

  return !isEmpty(tables) || isLiveCasinoGame(game) ? (
    <CasinoGameIcon
      className={classNames(className, iconClass, {
        [styles.mosaic]: mosaicHint === MosaicContext.BigIcon
      })}
      game={game}
      race={liveRaceIcon ? race : undefined}
      icon={icon}
      preload={preload}
      scale={!noIconScale}
      mosaicHint={mosaicHint}
      onClick={isTouchSupported ? () => launchGame() : undefined}
      visible={isVisible}
      innerRef={ref}
      data-testid="wrapper"
    >
      {(preload || isVisible) && (
        <>
          {fsb && isAuthenticated && (
            <img
              src={fsbIcon}
              className={classNames(styles.fsbIcon, icon === 'icnHeroSquare' && styles.large)}
              alt={formatMessage(TXTCLI_Free_Spins)}
            />
          )}
          {tables[PlayType.Multi] && (
            <img
              src={tables[PlayType.Single] ? singleMultiIcon : multiIcon}
              className={styles.playIcon}
              alt=""
            />
          )}
          {favouriteIcon && isAuthenticated && favoritesEnabled && isMobile && (
            <FavIcon className={styles.isFavorite} onClick={onFavoriteClick} />
          )}
          <Ribbon {...game} />
          {jackpot && (
            <JackpotRibbon
              className={classNames(styles.jackpot, jackpotClassName)}
              jackpot={jackpot}
              currency={playMoney ? Currency.PlayMoney : jackpot.currency}
              key={name}
              visible={isVisible}
            />
          )}
          {isMobile && hasInfoRoute && <GameInfoIcon game={game} index={index} />}
          {!isTouchSupported && (
            <CasinoPlaySelector
              hover
              multiplayer={Object.keys(tables).length > 1}
              className={styles.selector}
              infoIconClass={infoIconClass}
              onClick={launchGame}
              size="small"
              game={game}
              favouriteIcon={favouriteIcon}
              hideFeatureButtons={!!race || hideFeatureButtons}
              index={index}
              count={count}
            />
          )}
        </>
      )}
    </CasinoGameIcon>
  ) : null;
};

export default CasinoGameTile;
